import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore } from '@/stores/ui';
import { IS_DEMO_MODE } from '@/api/demoMode';

interface MeridianNavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

function ConditionIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 15l4-4 3 3 5-7"/></svg>
  );
}

function BriefingIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M8 8h8" /><path d="M8 12h8" /><path d="M8 16h5" /></svg>
  );
}

function OverviewIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="18" height="7" rx="1"/></svg>
  );
}

function PredictionsIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19V5"/><path d="M4 19h16"/><path d="M8 15l3-4 3 2 4-6"/></svg>
  );
}

function SurveyIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <circle cx="9" cy="14" r="1.5" />
      <circle cx="13" cy="11" r="1.5" />
      <circle cx="17" cy="8" r="1.5" />
    </svg>
  );
}

function ProgressIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>
  );
}

function YieldIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h16"/><path d="M7 16V9"/><path d="M12 16V5"/><path d="M17 16v-4"/><path d="M6 9l6-4 6 7"/></svg>
  );
}

function MapIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" x2="9" y1="3" y2="18"/><line x1="15" x2="15" y1="6" y2="21"/></svg>
  );
}

function ClassificationIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
  );
}

function BaselinesIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16"/><path d="M6 16l4-4 3 2 5-7"/></svg>
  );
}

function ExportIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>
  );
}

function OpsIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v3"/><path d="M12 18v3"/><path d="M4.9 4.9l2.1 2.1"/><path d="M17 17l2.1 2.1"/><path d="M3 12h3"/><path d="M18 12h3"/><circle cx="12" cy="12" r="4"/></svg>
  );
}

function SettingsIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
  );
}

const navItems: MeridianNavItem[] = [
  { label: 'Overview', to: '/meridian/overview', icon: <OverviewIcon /> },
  { label: 'Briefing', to: '/meridian/briefing', icon: <BriefingIcon /> },
  { label: 'Predictions', to: '/meridian/predictions', icon: <PredictionsIcon /> },
  { label: 'Survey', to: '/meridian/survey', icon: <SurveyIcon /> },
  { label: 'Condition', to: '/meridian/condition', icon: <ConditionIcon /> },
  { label: 'Progress', to: '/meridian/progress', icon: <ProgressIcon /> },
  { label: 'Yield', to: '/meridian/yield', icon: <YieldIcon /> },
  { label: 'Map', to: '/meridian/map', icon: <MapIcon /> },
  { label: 'Classification', to: '/meridian/classification', icon: <ClassificationIcon /> },
  { label: 'Baselines', to: '/meridian/baselines', icon: <BaselinesIcon /> },
  { label: 'Exports', to: '/meridian/exports', icon: <ExportIcon /> },
  { label: 'Ops', to: '/meridian/ops', icon: <OpsIcon /> },
  { label: 'Settings', to: '/meridian/settings', icon: <SettingsIcon /> },
];

// The public demo is backed by a static snapshot (demo/server.mjs), which only
// implements the manifest / briefing / yield / condition / geographies /
// crop-capabilities / acreage endpoints; Survey reads bundled fixtures.
// Restrict navigation to surfaces that resolve against that read-only
// snapshot and lead with the evidence-status overview. Predictions, Progress,
// Exports, and Settings hit endpoints the snapshot does not serve.
const DEMO_NAV_ORDER = ['Overview', 'Briefing', 'Yield', 'Classification', 'Survey', 'Condition', 'Map', 'Baselines'];
const visibleNavItems: MeridianNavItem[] = IS_DEMO_MODE
  ? DEMO_NAV_ORDER.map((label) => navItems.find((item) => item.label === label)).filter(
      (item): item is MeridianNavItem => Boolean(item),
    )
  : navItems;

export function MeridianShell() {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore();
  const { user, tenant } = useAuth();
  const persistentSearch = location.search;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(216,236,226,0.95),_rgba(239,246,243,1)_46%,_rgba(240,248,255,0.9)_100%)]">
      <aside
        className={clsx(
          'flex h-full shrink-0 flex-col border-r border-canopy-100 bg-canopy-950 text-white transition-all duration-200',
          sidebarCollapsed ? 'w-20' : 'w-72',
        )}
      >
        <div className={clsx('border-b border-canopy-800 px-4 py-5', sidebarCollapsed && 'px-3')}>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-linear-to-br from-canopy-500 via-sky-600 to-sky-800 shadow-card">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="8" opacity="0.35" />
                <path d="M4 12h16" />
                <path d="M8 7.5c1.5 1 2.5 2.4 3 4.5" />
              </svg>
            </div>
            {!sidebarCollapsed && (
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-canopy-200">TerraYield</p>
                <p className="truncate text-lg font-semibold text-white">Meridian</p>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5" aria-label="Meridian navigation">
          <ul className="space-y-1.5">
            {visibleNavItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={{ pathname: item.to, search: persistentSearch }}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 rounded-2xl px-3 text-sm font-medium transition-colors',
                      sidebarCollapsed && 'justify-center px-0',
                      isActive
                        ? 'bg-canopy-50 text-canopy-800'
                        : 'text-canopy-100 hover:bg-canopy-900 hover:text-white',
                    )
                  }
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <span className="shrink-0">{item.icon}</span>
                  {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-canopy-800 p-3">
          <div className={clsx('mb-3 rounded-2xl bg-canopy-900/80 p-3', sidebarCollapsed && 'px-2 py-3 text-center')}>
            <p className="text-xs font-semibold text-white">{user?.fullName ?? user?.email ?? 'Analyst'}</p>
            {!sidebarCollapsed && tenant && <p className="mt-1 text-xs text-canopy-200">{tenant.name}</p>}
          </div>
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            className={clsx(
              'flex w-full items-center gap-2 rounded-2xl px-3 text-xs font-semibold text-canopy-100 transition-colors hover:bg-canopy-900 hover:text-white',
              sidebarCollapsed && 'justify-center px-0',
            )}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className={clsx('transition-transform', sidebarCollapsed && 'rotate-180')}>
              <ChevronLeftIcon />
            </span>
            {!sidebarCollapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="border-b border-canopy-100 bg-white/80 px-6 py-4 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-canopy-700">USDA analyst workspace</p>
              <h1 className="text-xl font-semibold text-charcoal">Meridian crop intelligence</h1>
            </div>
            <div className="rounded-full border border-canopy-100 bg-canopy-50 px-4 py-2 text-sm font-medium text-canopy-800">
              Corn Belt operational review
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-5 py-5 md:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
